<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';
import { dateLong, dateTile, timeRange } from './eventTime.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const event = inject('tiqCurrentEvent', null);
// A visitor never meets the slot's placeholder: with no real event
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

const tile = computed(() => dateTile(event));
const fullDate = computed(() => dateLong(event, true));
const timeStr = computed(() => timeRange(event));
const align = computed(() => (['left', 'center', 'right'].includes(props.config.align) ? props.config.align : 'left'));

// Classic lets the Style section recolor the date tile. Trailhead's
// tile is the accent tint with an accent month, so a chosen colour is
// honoured as the tile's background with white type over it.
const tileStyle = computed(() => {
    const bg = props.config.style?.date_bg;
    return bg ? { background: bg, color: '#fff' } : {};
});
const monthStyle = computed(() => (props.config.style?.date_bg ? { color: 'inherit' } : {}));
</script>

<template>
    <section v-if="event || editing" :id="config.anchor_id || null"
             class="th-block th-sec th-sec--tight th-ewhen"
             :class="`is-align-${align}`"
             :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-row th-ewhen__inner">
                <div class="th-date" :style="tileStyle">
                    <span class="th-date__m" :style="monthStyle">{{ tile.month }}</span>
                    <span class="th-date__d">{{ tile.day }}</span>
                </div>
                <div class="th-ewhen__text">
                    <p v-if="fullDate" class="th-h3 th-ewhen__date">{{ fullDate }}</p>
                    <p v-if="timeStr" class="th-mono th-ewhen__time">{{ timeStr }}</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-ewhen__inner { max-width: 820px; gap: 16px; }
.th-ewhen.is-align-center .th-ewhen__inner { justify-content: center; margin: 0 auto; }
.th-ewhen.is-align-right .th-ewhen__inner { justify-content: flex-end; margin-left: auto; }
.th-ewhen__text { display: flex; flex-direction: column; gap: 4px; }
.th-ewhen__date { color: var(--text-heading, inherit); }
.th-ewhen__time { color: var(--text-sub, var(--th-meta)); }
</style>
